import React, { useState, Children, Component } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ChevronDownIcon, GiftIcon, HeartIcon, UsersIcon, FlameIcon, MoonIcon, AlertCircleIcon, BookOpenIcon, PenToolIcon, ClockIcon } from 'lucide-react';
import BackButton from '../components/UI/BackButton';
import { useDarkMode } from '../context/DarkModeContext';
import { useDarkClasses } from '../hooks/useDarkClasses';
import { useRoleTheme } from '../hooks/useRoleTheme';
import { booksDatabase } from './book-detail/data';
import BookCover from './book-detail/BookCover';
import BookInfo from './book-detail/BookInfo';
import BookDetailsCard from './book-detail/BookDetailsCard';
import LatestReviews from './book-detail/LatestReviews';
import ContentCategories from './book-detail/ContentCategories';
// Spice/Darkness level maps
const spiceLevelMap: Record<number, {
  label: string;
  color: string;
}> = {
  1: {
    label: 'Mild',
    color: 'bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300'
  },
  2: {
    label: 'Warm',
    color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
  },
  3: {
    label: 'Hot',
    color: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300'
  },
  4: {
    label: 'Steamy',
    color: 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300'
  },
  5: {
    label: 'Scorching',
    color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
  }
};
const darknessLevelMap: Record<number, {
  label: string;
  color: string;
}> = {
  1: {
    label: 'Shadow',
    color: 'bg-slate-100 text-slate-700 dark:bg-slate-800/50 dark:text-slate-300'
  },
  2: {
    label: 'Grey',
    color: 'bg-slate-200 text-slate-700 dark:bg-slate-700/50 dark:text-slate-300'
  },
  3: {
    label: 'Abyss',
    color: 'bg-gray-200 text-gray-700 dark:bg-gray-700/50 dark:text-gray-300'
  },
  4: {
    label: 'Pitch Black',
    color: 'bg-gray-300 text-gray-800 dark:bg-gray-600/50 dark:text-gray-200'
  },
  5: {
    label: 'The Void',
    color: 'bg-gray-400 text-gray-900 dark:bg-gray-500/50 dark:text-gray-100'
  }
};
// Story Accordion Item Component
interface StoryAccordionProps {
  contributor: {
    authorName: string;
    authorSlug: string;
    storyTitle: string;
    pages?: number;
    pov?: string;
    tense?: string;
    spiceLevel?: number;
    darknessLevel?: number;
    contentWarnings?: string[];
    tropes?: string[];
    subgenres?: string[];
    fullBookId?: string;
    fullBookTitle?: string;
  };
  index: number;
}
const StoryAccordion: React.FC<StoryAccordionProps> = ({
  contributor,
  index
}) => {
  const {
    darkMode
  } = useDarkMode();
  const dk = useDarkClasses();
  const rt = useRoleTheme();
  const [isOpen, setIsOpen] = useState(false);
  const hasMetadata = contributor.pov || contributor.tense || contributor.spiceLevel || contributor.darknessLevel || contributor.contentWarnings && contributor.contentWarnings.length > 0 || contributor.tropes && contributor.tropes.length > 0 || contributor.subgenres && contributor.subgenres.length > 0 || contributor.fullBookId;
  return <div className={`rounded-xl border overflow-hidden transition-all duration-200 ${dk.border} ${dk.card} ${isOpen ? 'ring-2 ring-indigo-500/30' : ''}`} data-id="element-6231" data-mp="1xgobgr89d55q">
      {/* Header - Always visible */}
      <button type="button" onClick={() => setIsOpen(!isOpen)} className={`w-full flex items-center justify-between px-4 py-4 sm:px-5 transition-colors text-left ${dk.dropdownItemHover}`} data-id="element-6232" data-mp="2q4k6zsm3cr0x">
        <div className="flex-1 min-w-0" data-id="element-6233" data-mp="2g5umds2t8sgz">
          <div className="flex items-center gap-2 mb-1" data-id="element-6234" data-mp="2g90b367j1sc6">
            <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${dk.badgeNeutral}`} data-id="element-6235" data-mp="2l4fmzslz5e5y">
              #{index + 1}
            </span>
            <h3 className={`text-base sm:text-lg font-bold ${dk.textHeading} truncate`} data-id="element-6236" data-mp="19r3hldakgnho">
              "{contributor.storyTitle}"
            </h3>
          </div>
          <p className={`text-sm ${dk.textSecondary}`} data-id="element-6237" data-mp="1k9qr2nl6w4ew">
            by{' '}
            <Link to={`/author/${contributor.authorSlug}`} className={`${rt.accentLink} hover:underline`} onClick={e => e.stopPropagation()} data-id="element-6238" data-mp="1k7nt9hdsp6zm">
              {contributor.authorName}
            </Link>
            {contributor.pages && <span className={`ml-2 ${dk.textMuted}`} data-id="element-6239" data-mp="27wyuezz253ea">
                • {contributor.pages} pages
              </span>}
          </p>
        </div>
        <div className="flex items-center gap-3 ml-4" data-id="element-6240" data-mp="1yx3haplxyybm">
          {/* Quick indicators when collapsed */}
          {!isOpen && <div className="hidden sm:flex items-center gap-2" data-id="element-6241" data-mp="1u1efefmg95c5">
              {contributor.spiceLevel && contributor.spiceLevel > 0 && <FlameIcon className={`h-4 w-4 ${contributor.spiceLevel >= 4 ? 'text-rose-500' : 'text-orange-400'}`} title={`Spice: ${spiceLevelMap[contributor.spiceLevel]?.label}`} data-id="element-6242" data-mp="ijlnr43h33a0" />}
              {contributor.darknessLevel && contributor.darknessLevel >= 3 && <MoonIcon className="h-4 w-4 text-gray-500" title={`Darkness: ${darknessLevelMap[contributor.darknessLevel]?.label}`} data-id="element-6243" data-mp="idm71uz3573c" />}
              {contributor.contentWarnings && contributor.contentWarnings.length > 0 && <AlertCircleIcon className="h-4 w-4 text-amber-500" title="Has content warnings" data-id="element-6244" data-mp="w3xz5tvo75uh" />}
              {contributor.fullBookId && <BookOpenIcon className="h-4 w-4 text-indigo-500" title="Expanded into full novel" data-id="element-6245" data-mp="161m75liae9ul" />}
            </div>}
          <ChevronDownIcon className={`h-5 w-5 transition-transform duration-200 flex-shrink-0 ${isOpen ? 'rotate-180' : ''} ${dk.textMuted}`} data-id="element-6246" data-mp="37x6b1qy5y02i" />
        </div>
      </button>

      {/* Expanded Content */}
      <div className={`transition-all duration-200 ease-in-out overflow-hidden ${isOpen ? 'max-h-[1000px] opacity-100' : 'max-h-0 opacity-0'}`} data-id="element-6247" data-mp="39sjvqv02zkx9">
        <div className={`px-4 pb-4 sm:px-5 sm:pb-5 border-t ${dk.isDark ? 'border-gray-700' : 'border-gray-100'}`} data-id="element-6248" data-mp="34sfdazfm4g4e">
          <div className="pt-4 space-y-4" data-id="element-6249" data-mp="3duohz8o79f9v">
            {/* Writing Style Row */}
            {(contributor.pov || contributor.tense) && <div className="flex flex-wrap gap-3" data-id="element-6250" data-mp="3shzxtmrezlfs">
                {contributor.pov && <div className="flex items-center gap-2" data-id="element-6251" data-mp="3nlj7qn0nmx02">
                    <PenToolIcon className={`h-4 w-4 ${dk.textMuted}`} data-id="element-6252" data-mp="5ebvmbhht5v5" />
                    <span className={`text-sm ${dk.textSecondary}`} data-id="element-6253" data-mp="5cikojzujzhc">
                      <span className={`font-medium ${dk.textPrimary}`} data-id="element-6254" data-mp="1ekylq1dhti6">
                        POV:
                      </span>{' '}
                      {contributor.pov}
                    </span>
                  </div>}
                {contributor.tense && <div className="flex items-center gap-2" data-id="element-6255" data-mp="azkkq5kxjgxb">
                    <ClockIcon className={`h-4 w-4 ${dk.textMuted}`} data-id="element-6256" data-mp="fzp01sa3q5ly" />
                    <span className={`text-sm ${dk.textSecondary}`} data-id="element-6257" data-mp="fxvw4kqvhzhn">
                      <span className={`font-medium ${dk.textPrimary}`} data-id="element-6258" data-mp="1a0z4s5u18yr">
                        Tense:
                      </span>{' '}
                      {contributor.tense}
                    </span>
                  </div>}
              </div>}

            {/* Spice & Darkness Levels */}
            {(contributor.spiceLevel || contributor.darknessLevel) && <div className="flex flex-wrap gap-2" data-id="element-6259" data-mp="3eidwccu5dvou">
                {contributor.spiceLevel && contributor.spiceLevel > 0 && <span className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 ${rt.badgeRadius} font-medium ${spiceLevelMap[contributor.spiceLevel]?.color || ''}`} data-id="element-6260" data-mp="3jiibnzjbkkdh">
                    <FlameIcon className="h-3.5 w-3.5" data-id="element-6261" data-mp="3txj90ikgd2wa" />
                    Spice: {spiceLevelMap[contributor.spiceLevel]?.label}
                  </span>}
                {contributor.darknessLevel && contributor.darknessLevel > 0 && <span className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 ${rt.badgeRadius} font-medium ${darknessLevelMap[contributor.darknessLevel]?.color || ''}`} data-id="element-6262" data-mp="3g0cs6tjahe9p">
                    <MoonIcon className="h-3.5 w-3.5" data-id="element-6263" data-mp="36a19u1yuz2ja" />
                    Darkness:{' '}
                    {darknessLevelMap[contributor.darknessLevel]?.label}
                  </span>}
              </div>}

            {/* Subgenres */}
            {contributor.subgenres && contributor.subgenres.length > 0 && <div data-id="element-6264" data-mp="17shbnd0w8of7">
                <span className={`text-xs font-semibold uppercase tracking-wider ${dk.textMuted} mb-2 block`} data-id="element-6265" data-mp="17qwj1sy53fjz">
                  Subgenres
                </span>
                <div className="flex flex-wrap gap-1.5" data-id="element-6266" data-mp="1hr6vhbccoiy3">
                  {contributor.subgenres.map((subgenre, idx) => <span key={idx} className={`text-xs px-2.5 py-1 ${rt.badgeRadius} ${dk.isDark ? 'bg-indigo-900/30 text-indigo-300' : 'bg-indigo-100 text-indigo-700'}`} data-id="element-6267" data-mp="1lqxu2184c3d6" data-mpi={idx}>
                      {subgenre}
                    </span>)}
                </div>
              </div>}

            {/* Tropes */}
            {contributor.tropes && contributor.tropes.length > 0 && <div data-id="element-6268" data-mp="69xecvgp22zy">
                <span className={`text-xs font-semibold uppercase tracking-wider ${dk.textMuted} mb-2 block`} data-id="element-6269" data-mp="6agv48ulylce">
                  Tropes
                </span>
                <div className="flex flex-wrap gap-1.5" data-id="element-6270" data-mp="37u5vsyfyhwbi">
                  {contributor.tropes.map((trope, idx) => <span key={idx} className={`text-xs px-2.5 py-1 ${rt.badgeRadius} ${dk.isDark ? 'bg-pink-900/30 text-pink-300' : 'bg-pink-100 text-pink-700'}`} data-id="element-6271" data-mp="3cw4y04zymn6v" data-mpi={idx}>
                      {trope}
                    </span>)}
                </div>
              </div>}

            {/* Content Warnings */}
            {contributor.contentWarnings && contributor.contentWarnings.length > 0 && <div data-id="element-6272" data-mp="2y31gfxmdc9g5">
                  <span className={`text-xs font-semibold uppercase tracking-wider ${dk.textMuted} mb-2 flex items-center gap-1.5`} data-id="element-6273" data-mp="32vulynpnvlta">
                    <AlertCircleIcon className="h-3.5 w-3.5 text-amber-500" data-id="element-6274" data-mp="25ee1ejs7qgb1" />
                    Content Warnings
                  </span>
                  <div className="flex flex-wrap gap-1.5" data-id="element-6275" data-mp="22wkcohhz2m3u">
                    {contributor.contentWarnings.map((warning, idx) => <span key={idx} className={`text-xs px-2.5 py-1 ${rt.badgeRadius} ${dk.isDark ? 'bg-amber-900/30 text-amber-300' : 'bg-amber-100 text-amber-700'}`} data-id="element-6276" data-mp="1xy8yxvok3rgc" data-mpi={idx}>
                        {warning}
                      </span>)}
                  </div>
                </div>}

            {/* Full Novel Link */}
            {contributor.fullBookId && <div className={`mt-4 p-3 rounded-lg border ${dk.isDark ? 'bg-indigo-900/20 border-indigo-800' : 'bg-indigo-50 border-indigo-200'}`} data-id="element-6277" data-mp="1a6txe54lte65">
                <div className="flex items-center gap-2 mb-1" data-id="element-6278" data-mp="116oy6i9kdm32">
                  <BookOpenIcon className={`h-4 w-4 ${dk.isDark ? 'text-indigo-400' : 'text-indigo-600'}`} data-id="element-6279" data-mp="115vphbr1omia" />
                  <span className={`text-xs font-semibold uppercase tracking-wider ${dk.isDark ? 'text-indigo-300' : 'text-indigo-700'}`} data-id="element-6280" data-mp="mk86vv0hpq1j">
                    Expanded into Full Novel
                  </span>
                </div>
                <Link to={`/book/${contributor.fullBookId}`} className={`text-sm font-medium ${rt.accentLink} hover:underline`} data-id="element-6281" data-mp="cnwmehqqxtw0">
                  {contributor.fullBookTitle || 'View Full Book'} →
                </Link>
              </div>}

            {/* No metadata fallback */}
            {!hasMetadata && <p className={`text-sm ${dk.textMuted} italic`} data-id="element-6282" data-mp="vk4vjwmvqowl">
                Detailed story information coming soon.
              </p>}
          </div>
        </div>
      </div>
    </div>;
};
const AnthologyDetail = () => {
  const {
    darkMode
  } = useDarkMode();
  const dk = useDarkClasses();
  const rt = useRoleTheme();
  const {
    id
  } = useParams<{
    id: string;
  }>();
  // Default to anthology-1 if not found
  const book = booksDatabase[id || 'anthology-1'] || booksDatabase['anthology-1'];
  const [selectedFormat, setSelectedFormat] = useState(book.formats[0]);
  const [isAudioPlaying, setIsAudioPlaying] = useState(false);
  const [expandedSection, setExpandedSection] = useState<string | null>(null);
  // Mock cart function
  const setCartItemCount = (count: any) => console.log('Added to cart');
  return <div className={darkMode ? 'bg-gray-900 text-gray-100' : 'bg-white text-gray-900'} data-id="element-6283" data-mp="2utfwwlyxbn7m">
      {/* Charity Banner */}
      <div className={`${darkMode ? 'bg-rose-900/30 border-rose-800' : 'bg-rose-50 border-rose-100'} border-b py-3`} data-id="element-6284" data-mp="33tcgi96ghvvt">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-center gap-3 text-center" data-id="element-6285" data-mp="2yrvl2t6qr1mm">
          <GiftIcon className={`h-5 w-5 ${darkMode ? 'text-rose-400' : 'text-rose-500'}`} data-id="element-6286" data-mp="e37pv914dan9" />
          <span className={`font-medium ${darkMode ? 'text-rose-200' : 'text-rose-800'}`} data-id="element-6287" data-mp="19dxkugdmt4wu">
            100% of proceeds from this anthology benefit the Children's Literacy
            Foundation.
          </span>
          <Link to="/donations/nonprofit/childrens-literacy-foundation" className={`text-sm font-bold underline underline-offset-2 ${darkMode ? 'text-rose-300 hover:text-rose-200' : 'text-rose-700 hover:text-rose-900'}`} data-id="element-6288" data-mp="1ja0l1goxath5">
            Learn More
          </Link>
        </div>
      </div>

      {/* Book Header */}
      <div className={`${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'} py-6 sm:py-8 border-b`} data-id="element-6289" data-mp="22232gu9e2dsg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-6290" data-mp="26zl8ocw0uewr">
          <div className="mb-6" data-id="element-6291" data-mp="2c0ivx3vw1z54">
            <BackButton data-id="element-6292" data-mp="2c1kahic453rl" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8" data-id="element-6293" data-mp="2g1knqznh78x2">
            <BookCover book={book} isAudioPlaying={isAudioPlaying} setIsAudioPlaying={setIsAudioPlaying} data-id="element-6294" data-mp="2fzhzol5vvpuz" />

            {/* Custom Anthology Info Section */}
            <div className="md:col-span-2 space-y-4" data-id="element-6295" data-mp="f0axql1f3fe">
              <div data-id="element-6296" data-mp="3rk04bbthecac">
                <div className="flex items-center gap-2 mb-2" data-id="element-6297" data-mp="3rj73ssl48j0n">
                  <span className="bg-rose-100 text-rose-800 dark:bg-rose-900/50 dark:text-rose-300 text-xs font-bold px-2.5 py-0.5 rounded-full flex items-center gap-1" data-id="element-6298" data-mp="3mkni8tafp98t">
                    <HeartIcon className="h-3 w-3 fill-current" data-id="element-6299" data-mp="j35ccre82cb1" /> Charity
                    Anthology
                  </span>
                  <span className="bg-indigo-100 text-indigo-800 dark:bg-indigo-900/50 dark:text-indigo-300 text-xs font-bold px-2.5 py-0.5 rounded-full flex items-center gap-1" data-id="element-6300" data-mp="ebnam14dk7p0">
                    <UsersIcon className="h-3 w-3" data-id="element-6301" data-mp="16sg4xllf50e3" />{' '}
                    {book.contributors?.length || 0} Authors
                  </span>
                </div>
                <h1 className={`text-2xl sm:text-3xl font-bold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-6302" data-mp="11zwpsk731x76">
                  {book.title}
                </h1>
                <p className={`text-lg mb-3 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-6303" data-mp="30djfrik0d465">
                  A collection by{' '}
                  {book.contributors?.slice(0, 3).map((c: any) => c.authorName).join(', ')}
                  {(book.contributors?.length || 0) > 3 && ` + ${(book.contributors?.length || 0) - 3} more`}
                </p>
              </div>

              {/* Re-use standard BookInfo for the rest (rating, tags, formats, buttons) */}
              <BookInfo book={{
              ...book,
              title: '',
              author: ''
            }} selectedFormat={selectedFormat} setSelectedFormat={setSelectedFormat} setCartItemCount={setCartItemCount} hideDonationCallout data-id="element-6304" data-mp="28kix1ughnqfy" />
            </div>
          </div>
        </div>
      </div>

      {/* Book Details */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-id="element-6305" data-mp="ppo6vsmf87vv">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-6306" data-mp="pqzansfv9fmz">
          <div className="order-2 md:order-1" data-id="element-6307" data-mp="kxmodoyntkxe">
            <BookDetailsCard book={book} data-id="element-6308" data-mp="znc1mik11fe1" />
          </div>

          <div className="md:col-span-2 space-y-8 order-1 md:order-2" data-id="element-6309" data-mp="zlr88w9g82hr">
            {/* Individual Stories - Accordion */}
            {book.contributors && book.contributors.length > 0 && <section data-id="element-6310" data-mp="18ngy3xnomtdf">
                <div className="flex items-center justify-between mb-4" data-id="element-6311" data-mp="18lwdazr66osr">
                  <h2 className={`text-2xl font-bold ${dk.textHeading}`} data-id="element-6312" data-mp="14mx24xeuhvsv">
                    Included Stories
                  </h2>
                  <span className={`text-sm ${dk.textMuted}`} data-id="element-6313" data-mp="3ah6qtgi3a5ug">
                    {book.contributors.length} stories
                  </span>
                </div>
                <div className="space-y-3" data-id="element-6314" data-mp="3uy71dkb3nn6l">
                  {book.contributors.map((contributor: any, idx: number) => <StoryAccordion key={idx} contributor={contributor} index={idx} data-id="element-6315" data-mp="3qaxzcdj9z6u" data-mpi={idx} />)}
                </div>
              </section>}

            {/* Content Categories */}
            <ContentCategories book={book} expandedSection={expandedSection} setExpandedSection={setExpandedSection} data-id="element-6316" data-mp="3c1ozia2je6wm" />
          </div>
        </div>
      </div>

      {/* Latest Reviews */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12" data-id="element-6317" data-mp="3q056810efxer">
        <LatestReviews book={book} data-id="element-6318" data-mp="3px98acojx8qb" />
      </div>
    </div>;
};
export default AnthologyDetail;